import React from 'react';
import { GhgDataEntryCompletedType } from './interfaces';
import { useGhgDataEntriesCompleted } from './hooks';
import { useGhgOrganization } from 'src/common/hooks';
import { timeConverter } from 'src/utils/timeConverter';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormGroupProps,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { format } from 'date-fns';

interface Props extends FormGroupProps {
  selectedDate: Date;
  type: GhgDataEntryCompletedType;
}

const dateFormat = 'dd.MM.yyyy';

export default function DataEntryCompletedCheckbox(props: Props) {
  const { selectedDate, type } = props;
  const { t } = useTranslation(undefined, {
    keyPrefix: 'ghg.dataEntryCompletedCheckbox',
  });
  const org = useGhgOrganization();
  const queryClient = useQueryClient();
  const period = timeConverter(selectedDate, org.co2.reporting_freq);
  const { isLoading, _data, create, _delete } = useGhgDataEntriesCompleted({
    organization_id: org.id,
    period,
  });

  const checked = _data.find((entry) => entry.data_type === type);

  // RG 28.08 review: whether the target can be set (pop-up 2, the readiness
  // checklist) follows the completion marks, so a change here must reach those
  // queries in the same session -- not only after a reload. The prefix covers
  // the target state and the readiness; observers that are disabled (Letter
  // not accepted) are not fetched by this.
  const refreshCommitmentTarget = () => {
    queryClient.invalidateQueries(['ghg-supplier', 'commitment-target']);
  };

  const handleChange = () => {
    if (isLoading) return;
    if (checked === undefined) {
      create.mutate(
        {
          data_type: type,
          organization_id: org.id,
          period,
        },
        { onSuccess: refreshCommitmentTarget }
      );
    } else {
      _delete.mutate(checked.id, { onSuccess: refreshCommitmentTarget });
    }
  };

  const label =
    checked === undefined
      ? t('labelNotCompleted')
      : t('labelCompleted', {
          date: format(new Date(checked.completed_at), dateFormat),
          user: checked.completed_by,
        });

  return (
    <FormGroup sx={{ mt: '-32px', mb: '12px', ...props.sx }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={checked !== undefined}
            onClick={() => handleChange()}
          />
        }
        label={label}
      />
    </FormGroup>
  );
}
